import { useSelector } from 'react-redux'
import {
  Button,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  Autocomplete
} from '@mui/material'
import { AttachFile, Clear, CloseRounded } from '@mui/icons-material'
import Grid from '@mui/material/Unstable_Grid2'
import TabPanel from '../../../../shared/components/TabPanel'
import { IRootState } from '../../../../../redux/types'
import { useTranslation } from 'react-i18next'
import { formsStyles } from '../../../../shared/styles/FormsStyles'
import useTabState from '@/contexts/shared/hooks/useTabState'
import { StyledDialog } from '@/contexts/shared/styles/StyledDialog'
import useInitialState from '@/contexts/backoffice/events/ui/hooks/modal/useInitialState'
import useEventModalState from '../hooks/modal/useEventModalState'
import useHandleSubmit from '../hooks/modal/useHandleSubmit'
import { StyledTabs } from '@/contexts/shared/components/StyledTabs'
import { CustomTab } from '@/contexts/backoffice/users/ui/components/modals/create/CustomTab'
import {
  CountryAutocomplete,
  LanguageMultiAutocomplete,
  StatusAutocomplete,
  VisibilityAutocomplete,
  ColorPicker,
  DateTimePicker,
  RoleAutocomplete,
  BrandAutocomplete,
  LineAutocomplete,
  CustomTextField,
  CancelButton,
  SaveButton
} from '@/contexts/shared/components/inputs'

function EventsModal() {
  const theme = useTheme()
  const formClasses = formsStyles(theme)
  const { t } = useTranslation(['global'])

  const { showForm } = useSelector((state: IRootState) => state.event)
  const { isValidForm, errorForm, errorTab, msgForm } = useSelector((state: IRootState) => state.validator)

  const { methods, types } = useInitialState()
  const { eventState, handleInputChange, handleInputChangeFile, handleRemoveImage, STATUS, VISIBILITY } = useEventModalState()
  const { currentTab, setCurrentTab, a11yProps } = useTabState()
  const { handleSubmit, handleCloseModalEvent } = useHandleSubmit(eventState)

  return (
    <StyledDialog
      open={showForm}
      onClose={handleCloseModalEvent}
      aria-labelledby="event-dialog-title"
      aria-describedby="event-dialog-description"
    >
      <DialogTitle id="event-dialog-title">
        <Grid container justifyContent="space-between">
          <Typography variant="h6">
            {eventState.id !== null ? t('edit') : t('create')} {t('event')}
          </Typography>
          <Button onClick={handleCloseModalEvent} color="primary" variant="text">
            <CloseRounded />
          </Button>
        </Grid>
      </DialogTitle>
      <DialogContent sx={{ pb: 3, pt: 2 }}>
        <Grid container alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Grid xs={12} sm={6} md={eventState.brand != null ? 6 : 8}>
            <CustomTextField inputName="title" value={eventState.title} handleInputChange={handleInputChange} required />
          </Grid>
          <Grid xs={12} sm={6} md={eventState.brand != null ? 3 : 4}>
            <BrandAutocomplete value={eventState.brand} handleInputChange={handleInputChange} />
          </Grid>
          {eventState.brand !== null && (
            <Grid xs={12} sm={6} md={3}>
              <LineAutocomplete value={eventState.line} brand={eventState.brand} handleInputChange={handleInputChange} />
            </Grid>
          )}
        </Grid>

        <Grid xs={12}>
          <StyledTabs value={currentTab} onChange={(_, value) => setCurrentTab(value)} aria-label="disabled tabs example">
            <CustomTab label={t('general')} {...a11yProps(0)} error={errorTab.general} />
            <CustomTab label={t('add-ons')} {...a11yProps(1)} error={errorTab.addsOns} />
          </StyledTabs>
        </Grid>

        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={4}>
              <RoleAutocomplete value={eventState.role} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <CountryAutocomplete value={eventState.country} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <LanguageMultiAutocomplete value={eventState.languages} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <StatusAutocomplete value={eventState.status} handleInputChange={handleInputChange} options={STATUS} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <VisibilityAutocomplete value={eventState.visibility} handleInputChange={handleInputChange} options={VISIBILITY} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              {eventState.file ? (
                <TextField
                  label={t('image')}
                  fullWidth
                  margin="normal"
                  variant="filled"
                  value={eventState.file.name}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleRemoveImage} color="error">
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              ) : (
                <TextField
                  id="txt-file-image"
                  label={t('image')}
                  fullWidth
                  margin="normal"
                  type="file"
                  error={errorForm.file_image}
                  helperText={msgForm.file_image}
                  onChange={handleInputChangeFile}
                  InputLabelProps={{ shrink: true }}
                  variant="filled"
                />
              )}
              {eventState.filePreview && (
                <Box component="div" p={1} my={2} borderRadius={1} style={{ backgroundColor: '#FAFAFA' }}>
                  <Typography variant="body2">{`${t('admin.library.preview')}:`}</Typography>
                  <img
                    src={eventState.filePreview}
                    style={{ width: '100%', height: 'auto', marginTop: '8px', borderRadius: '4px' }}
                    alt={eventState.title}
                  />
                </Box>
              )}
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <Autocomplete
                id="combo-box-types"
                disableClearable
                value={types.find((type: any) => type.id === eventState.type)}
                onChange={(_, value) => handleInputChange('type', value ? value.id : value)}
                options={types}
                getOptionLabel={(option: any) => (option.id !== undefined ? option.name : '')}
                renderInput={params => <TextField {...params} label={t('type')} variant="filled" InputLabelProps={{ shrink: true }} />}
              />
              <FormHelperText error={errorForm.type}>{msgForm.type}</FormHelperText>
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <DateTimePicker inputName="startDate" label="start-date" value={eventState.startDate} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <DateTimePicker
                inputName="endDate"
                label="end-date"
                value={eventState.endDate}
                minDate={eventState.startDate}
                handleInputChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={4}>
              <CustomTextField inputName="link" value={eventState.link} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <CustomTextField inputName="text_button" value={eventState.text_button} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <Autocomplete
                id="combo-box-method"
                value={methods.find((method: any) => method.id === eventState.method)}
                onChange={(_, value) => handleInputChange('method', value ? value.id : value)}
                options={methods}
                getOptionLabel={(option: any) => (option.id !== undefined ? option.name : '')}
                renderInput={params => (
                  <TextField
                    required
                    {...params}
                    error={errorForm.method}
                    helperText={msgForm.method}
                    label={t('admin.events.design-mode')}
                    variant="filled"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            {eventState.method === 0 && (
              <>
                <Grid xs={12} sm={12}>
                  <CustomTextField inputName="description" value={eventState.description} handleInputChange={handleInputChange} />
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <CustomTextField inputName="speaker" value={eventState.speaker} handleInputChange={handleInputChange} />
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {t('background-color')}
                  </Typography>
                  <div className="input-color">
                    <ColorPicker
                      inputName="background_color"
                      initialValue={eventState.background_color.hex}
                      handleInputChange={handleInputChange}
                    />
                  </div>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {t('text-color')}
                  </Typography>
                  <div className="input-color">
                    <ColorPicker inputName="text_color" initialValue={eventState.text_color.hex} handleInputChange={handleInputChange} />
                  </div>
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>
      </DialogContent>
      <DialogActions sx={!isValidForm && Object.keys(msgForm).length > 0 ? formClasses.errorDialogActions : undefined}>
        <CancelButton onClick={handleCloseModalEvent}>{t('cancel')}</CancelButton>
        <SaveButton autoFocus id="btn-handlelogin" onClick={() => handleSubmit()}>
          {eventState.id !== null ? t('save') : t('admin.events.create-event')}
        </SaveButton>
      </DialogActions>
    </StyledDialog>
  )
}

export default EventsModal
